import { PayloadAction } from "@reduxjs/toolkit";
import { IEffortActual } from "app/containers/Features/containers/Ticket/types";
import { IPagination } from "app/types";
import { createSlice } from "utils/@reduxjs/toolkit";
import { getTimeDuration } from "utils/hooks/useTimeTracker";
import {
  ISelectedTicketDetails,
  ITimeSheet,
  ITimeSheetData,
  ITimeSheetTicketListItem,
  ITimeTrackerForm,
} from "../types";
import { timeSheetInitialState } from "./initialState";

export const timeSheetSlice = createSlice({
  name: "timeSheetState",
  initialState: timeSheetInitialState,
  reducers: {
    // TimeSheet --
    setTimeSheetLoading: (state, action: PayloadAction<boolean>) => {
      state.timeSheet.loading = action.payload;
    },

    setTimeSheetLoadingMore: (state, action: PayloadAction<boolean>) => {
      state.timeSheet.loadingMore = action.payload;
    },

    setTimeSheetData: (
      state,
      action: PayloadAction<{ data: ITimeSheet[]; pagination: IPagination }>,
    ) => {
      const { data, pagination } = action.payload;

      if (!data) return;

      if (pagination.page === 1) {
        state.timeSheet.data = data;
      } else {
        state.timeSheet.data = [...(state.timeSheet.data || []), ...data];
      }
      state.timeSheet.pagination = pagination;
    },

    timeSheetRequest: (state) => {},

    setTimeSheetPagination: (state, action: PayloadAction<IPagination>) => {
      state.timeSheet.pagination = action.payload;
    },

    clearTimeSheetData: (state) => {
      state.timeSheet = timeSheetInitialState.timeSheet;
    },
    // --

    // TimeSheet Entry Delete --
    setTimeSheetEntryDeleteLoading: (state, action: PayloadAction<boolean>) => {
      state.timeSheetEntryDeleteLoading = action.payload;
    },

    deleteTimeSheetEntryRequest: (
      state,
      action: PayloadAction<{
        effortId: string;
        callback?: () => void;
      }>,
    ) => {
      state.timeSheetEntryDeleteLoading = true;
    },

    removeTimeSheetEntry: (
      state,
      action: PayloadAction<{
        effortId: string;
      }>,
    ) => {
      const { effortId } = action.payload;

      state.timeSheet.data.forEach((entry) => {
        const effortIndex = entry.data.findIndex(
          (effort) => effort._id === effortId,
        );

        if (effortIndex !== -1) {
          const deletedEffort = entry.data[effortIndex].effort;

          const totalMinutes =
            entry.totalTime.hours * 60 +
            entry.totalTime.minutes -
            (deletedEffort.hours * 60 + deletedEffort.minutes);

          entry.totalTime.hours = Math.floor(totalMinutes / 60);
          entry.totalTime.minutes = totalMinutes % 60;

          entry.data.splice(effortIndex, 1);
        }
      });

      state.timeSheet.data = state.timeSheet.data.filter(
        (entry) => entry.data.length > 0,
      );
    },

    setTimeTrackerForm: (state, action: PayloadAction<ITimeTrackerForm>) => {
      state.timeTrackerForm = action.payload;
    },

    // effort actual update api call
    effortActualRequest: (
      state,
      action: PayloadAction<{
        callback: (data: {
          form: ITimeTrackerForm;
          response: IEffortActual;
        }) => void;
        isActiveTimer?: boolean;
      }>,
    ) => {
      state.effortActualRequestLoading = true;
    },

    setEffortActualRequestLoading: (state, action: PayloadAction<boolean>) => {
      state.effortActualRequestLoading = action.payload;
    },

    setEnableTimeTrackerPreview(state, action: PayloadAction<boolean>) {
      state.enableTimeTrackerPreview = action.payload;
    },

    setEffortActualSingleValue(
      state,
      action: PayloadAction<{
        field: keyof ITimeTrackerForm;
        value: string | number | boolean | Date;
      }>,
    ) {
      const { field, value } = action.payload;
      const effort = state.timeTrackerForm;

      if (field === "startTime") {
        const newStart = value as string;

        effort.startTime = newStart;

        const durationSeconds = effort.hours * 3600 + effort.minutes * 60;

        const newEnd = new Date(
          new Date(newStart).getTime() + durationSeconds * 1000,
        ).toISOString();

        effort.endTime = newEnd;

        return;
      }

      if (field === "endTime") {
        const newEnd = value as string;

        effort.endTime = newEnd;

        const start = new Date(effort.startTime).getTime();
        const end = new Date(newEnd).getTime();

        if (end <= start) {
          effort.hours = 0;
          effort.minutes = 0;
          return;
        }

        const diffInSeconds = Math.floor((end - start) / 1000);

        effort.hours = Math.floor(diffInSeconds / 3600);
        effort.minutes = Math.floor((diffInSeconds % 3600) / 60);

        return;
      }

      if (field === "date") {
        const newDate = new Date(value as string);
        effort.date = value as string;

        const start = new Date(effort.startTime);

        start.setFullYear(
          newDate.getFullYear(),
          newDate.getMonth(),
          newDate.getDate(),
        );

        effort.startTime = start.toISOString();

        const { hours, minutes } = getTimeDuration(
          effort.startTime,
          effort.endTime,
        );

        effort.hours = hours;
        effort.minutes = minutes;

        return;
      }

      effort[field] = value as never;
    },

    setActiveTimerForm(state, action: PayloadAction<ITimeTrackerForm>) {
      state.activeTimerForm = action.payload;
    },

    updateTimeSheetEntryLocally: (
      state,
      action: PayloadAction<{
        timeSheetId: string;
        effortId: string;
        updates: Partial<IEffortActual>;
      }>,
    ) => {
      const { timeSheetId, effortId, updates } = action.payload;

      const entry = state.timeSheet.data.find((e) => e._id === timeSheetId);

      if (!entry) return;

      for (const effortItem of entry.data) {
        if (effortItem._id === effortId) {
          // Update effort
          effortItem.effort = {
            ...effortItem.effort,
            ...updates.effort,
          };

          break;
        }
      }

      // Recalculate total time
      const totalMinutes = entry.data.reduce(
        (sum, e) => sum + (e.effort.hours * 60 + e.effort.minutes),
        0,
      );
      entry.totalTime.hours = Math.floor(totalMinutes / 60);
      entry.totalTime.minutes = totalMinutes % 60;
    },

    addTimeSheetEntryLocally: (
      state,
      action: PayloadAction<{
        timeSheetId: string;
        newEntry: ITimeSheetData;
      }>,
    ) => {
      const { timeSheetId, newEntry } = action.payload;

      const existingGroup = state.timeSheet.data.find(
        (item) => item._id === timeSheetId,
      );

      if (existingGroup) {
        // Add entry to existing group
        existingGroup.data.push(newEntry);

        // Update total time
        existingGroup.totalTime.minutes += newEntry.effort.minutes;
        existingGroup.totalTime.hours += newEntry.effort.hours;

        // Normalize minutes to hours if needed
        if (existingGroup.totalTime.minutes >= 60) {
          existingGroup.totalTime.hours += Math.floor(
            existingGroup.totalTime.minutes / 60,
          );
          existingGroup.totalTime.minutes =
            existingGroup.totalTime.minutes % 60;
        }
      } else {
        // Create new group
        state.timeSheet.data.push({
          _id: timeSheetId,
          data: [newEntry],
          totalTime: {
            hours: newEntry.effort.hours,
            minutes: newEntry.effort.minutes,
          },
        });
      }
    },

    startTimerWithSwitch: (
      state,
      action: PayloadAction<{
        newTask: ISelectedTicketDetails;
      }>,
    ) => {
      state.effortActualRequestLoading = true;
    },

    resetTimeTrackerForms(state) {
      state.timeTrackerForm = timeSheetInitialState.timeTrackerForm;
    },

    setActiveTimerFormSingleValue(
      state,
      action: PayloadAction<{
        field: keyof ITimeTrackerForm;
        value: string | number | boolean | Date;
      }>,
    ) {
      const { field, value } = action.payload;
      const effort = state.activeTimerForm;

      if (field === "startTime") {
        const newStart = value as string;

        effort.startTime = newStart;

        const durationSeconds = effort.hours * 3600 + effort.minutes * 60;

        const newEnd = new Date(
          new Date(newStart).getTime() + durationSeconds * 1000,
        ).toISOString();

        effort.endTime = newEnd;

        return;
      }

      if (field === "endTime") {
        const newEnd = value as string;

        effort.endTime = newEnd;

        const start = new Date(effort.startTime).getTime();
        const end = new Date(newEnd).getTime();

        if (end <= start) {
          effort.hours = 0;
          effort.minutes = 0;
          return;
        }

        const diffInSeconds = Math.floor((end - start) / 1000);

        effort.hours = Math.floor(diffInSeconds / 3600);
        effort.minutes = Math.floor((diffInSeconds % 3600) / 60);

        return;
      }

      if (field === "date") {
        const newDate = new Date(value as string);
        effort.date = value as string;

        const start = new Date(effort.startTime);

        start.setFullYear(
          newDate.getFullYear(),
          newDate.getMonth(),
          newDate.getDate(),
        );

        effort.startTime = start.toISOString();

        const { hours, minutes } = getTimeDuration(
          effort.startTime,
          effort.endTime,
        );

        effort.hours = hours;
        effort.minutes = minutes;

        return;
      }

      effort[field] = value as never;
    },
    // --

    // Select Ticket List --
    fetchTimeSheetTicketList() {},

    setTimeSheetTicketListData(
      state,
      action: PayloadAction<{
        data: ITimeSheetTicketListItem[];
        pagination: IPagination;
      }>,
    ) {
      const { pagination, data } = action.payload;

      if (pagination.page === 1) {
        state.timeSheetTicketList.data = data;
      } else {
        state.timeSheetTicketList.data = [
          ...(state.timeSheetTicketList.data || []),
          ...data,
        ];
      }
      state.timeSheetTicketList.pagination = action.payload.pagination;
      state.timeSheetTicketList.loading = false;
    },

    setTimeSheetTicketListLoading(state, action: PayloadAction<boolean>) {
      state.timeSheetTicketList.loading = action.payload;
    },

    setTimeSheetTicketListMoreLoading(state, action: PayloadAction<boolean>) {
      state.timeSheetTicketList.moreLoading = action.payload;
    },

    setTimeSheetTicketListPagination(
      state,
      action: PayloadAction<IPagination>,
    ) {
      state.timeSheetTicketList.pagination = action.payload;
    },

    clearTimeSheetTicketList(state) {
      state.timeSheetTicketList = timeSheetInitialState.timeSheetTicketList;
    },

    setTimeSheetTicketSearchQuery(state, action: PayloadAction<string>) {
      state.timeSheetTicketListSearchQuery = action.payload;
    },
    // --
  },
});

export const timeSheetSliceName = timeSheetSlice?.name;

export const timeSheetSliceReducers = timeSheetSlice?.reducer;

export const timeSheetActions = timeSheetSlice?.actions;
